import type {
    DataTableColumn,
    FilterItemConfig,
} from '@/components/ui/data-table-customized'
import {
    createColumns,
    createDateFormatter,
} from '@/components/ui/data-table-customized'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import useUrlSearchParams from '@/hooks/useUrlSearchParams'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Avatar, Chip } from 'vnetwork-ui'
import {
    ChatRoomStatusOptions,
    PlatformOptions,
    getChatRoomStatusColor,
    getPlatformColor,
} from '../constants'
import { getAssignableUserOptions, getChatRooms } from '../services'
import { ChatRoom } from '../types/response'

interface LivechatFilters {
    status: string[]
    channel: string
    assignee: string
    dateRange: string[]
    dateRange_to: string[] // form-only field for daterangepicker UI
}

export const useLivechatList = () => {
    const { t } = useTranslation('livechat')

    const {
        params,
        filters,
        queryParams,
        setParam,
        setParams,
        setFilter,
        searchInputProps,
    } = useUrlSearchParams<LivechatFilters>({
        filterDefaults: {
            // Do not apply a status restriction until the user selects one.
            // Omitting `status` lets the Livechat API return every room state.
            status: [],
            channel: '',
            assignee: '',
            dateRange: [],
            dateRange_to: [],
        },
    })

    // Form control for FilterBar
    const { control } = useForm<LivechatFilters>({
        defaultValues: filters,
    })

    const { data, renderState } = useQueryWithHandler(
        {
            queryKey: [QUERY_KEYS.LIVECHAT_ROOMS, queryParams],
            queryFn: () =>
                getChatRooms({
                    ...queryParams,
                    page_size: queryParams.page_size || 20,
                }),
            placeholderData: true,
        },
        { showLoading: false }
    )

    // Keep every status in one chronological queue: newest chat room first.
    // `createdAt` is the time the visitor started the conversation.
    const rooms = [...(data?.results || [])].sort(
        (first, second) =>
            Date.parse(second.createdAt) - Date.parse(first.createdAt)
    )
    const pagination = data?.pagination

    // Column definitions
    const columns: DataTableColumn<ChatRoom>[] = createColumns<ChatRoom>([
        {
            key: 'status',
            title: t('status'),
            width: 140,
            render: (_, row) => {
                const statusOption = ChatRoomStatusOptions(t).find(
                    (opt) => opt.value === row.status
                )
                return (
                    <Chip
                        label={statusOption?.label || row.status}
                        size="small"
                        variant="contained"
                        color={getChatRoomStatusColor(row.status)}
                    />
                )
            },
        },
        {
            key: 'platform',
            title: t('platform'),
            width: 120,
            render: (_, row) =>
                row.platform ? (
                    <Chip
                        label={
                            row.platform.charAt(0).toUpperCase() +
                            row.platform.slice(1)
                        }
                        size="small"
                        variant="outlined"
                        color={getPlatformColor(row.platform)}
                    />
                ) : (
                    '-'
                ),
        },
        {
            key: 'users' as any,
            title: t('customer'),
            width: 250,
            render: (_, row) => {
                const customer = row.users?.find((u) => u.userType === 'user')
                if (!customer) return '-'
                return (
                    <div className="flex items-center gap-2">
                        <Avatar
                            src={customer.avatar}
                            alt={customer.name}
                            size="small"
                        />
                        <div>
                            <div className="text-sm">{customer.name}</div>
                            <div className="text-xs text-gray-400">
                                {customer.email}
                            </div>
                        </div>
                    </div>
                )
            },
        },
        {
            key: 'assignee' as any,
            title: t('assignee'),
            width: 200,
            render: (_, row) => {
                const emp = row.users?.find((u) => u.userType === 'employee')
                if (!emp) return '-'
                return (
                    <div className="flex items-center gap-2">
                        <Avatar src={emp.avatar} alt={emp.name} size="small" />
                        <span className="text-sm">{emp.name}</span>
                    </div>
                )
            },
        },
        {
            key: 'lastMessage',
            title: t('last-message'),
            width: 300,
            render: (_, row) => (
                <div className="truncate text-sm">
                    <span className="text-gray-400">
                        {row.lastMessageSenderName}:
                    </span>
                    <span className="ml-1">{row.lastMessage || '-'}</span>
                </div>
            ),
        },
        {
            key: 'unreadCount',
            title: t('unread'),
            width: 80,
            render: (val) =>
                val && Number(val) > 0 ? (
                    <span className="inline-flex items-center justify-center min-w-[16px] h-4 px-1 text-[10px] font-semibold text-white bg-red-500 rounded-full">
                        {Number(val) > 99 ? '99+' : val}
                    </span>
                ) : (
                    <span className="text-xs text-gray-300">-</span>
                ),
        },
        {
            key: 'updatedAt',
            title: t('last-updated'),
            cellClassName: 'text-right italic text-gray-400',
            width: 130,
            formatter: createDateFormatter({
                format: 'HH:mm DD/MM/YYYY',
            }),
        },
        {
            key: 'createdAt',
            title: t('created-at'),
            cellClassName: 'text-right italic text-gray-400',
            width: 130,
            formatter: createDateFormatter({
                format: 'HH:mm DD/MM/YYYY',
            }),
        },
    ])

    // Filter items for FilterBar
    const filterItems: FilterItemConfig[] = [
        {
            name: 'status',
            label: t('status'),
            type: 'multi-select',
            options: ChatRoomStatusOptions(t),
        },
        {
            name: 'channel',
            label: t('platform'),
            type: 'select',
            options: PlatformOptions(t),
        },
        {
            name: 'assignee',
            label: t('assignee'),
            type: 'async-select',
            asyncOptions: getAssignableUserOptions,
        },
        {
            name: 'dateRange',
            label: t('date-range'),
            type: 'daterangepicker',
            names: ['dateRange', 'dateRange_to'],
        },
    ]

    const handleSortChange = (sortBy: string, sortOrder: 'asc' | 'desc') =>
        setParams({ sort_by: sortBy, sort_order: sortOrder })
    const handlePageChange = (page: number) => setParam('page', page, false)
    const handlePageSizeChange = (pageSize: number) =>
        setParams({ page_size: pageSize, page: 1 })

    return {
        rooms,
        columns,
        pagination,
        params,
        filters,
        filterItems,
        control,
        searchInputProps,
        renderState,
        setFilter,
        handleSortChange,
        handlePageChange,
        handlePageSizeChange,
    }
}
